import { useLingui } from '@lingui/react/macro'
import type { ColumnFiltersState } from '@tanstack/react-table'
import { applyCardFilters } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsCardView/documentCardFilterBar/applyCardFilters'
import type { CardFilterValues } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsCardView/documentCardFilterBar/types'
import {
  DocumentTableColumns,
  type DocumentTableRow,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import { useDocumentSearchResultFilters } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocumentSearchResultFilters'
import type { FacetFilterId } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/browseAllFacets'
import {
  type DocumentTableColumnUniqueValues,
  getDocumentTableColumnUniqueValues,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentTablePage'
import type {
  SearchHistoryItem,
  SearchStateUpdate,
  ViewMode,
} from 'features/knowledgeDocumentViewer/types'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export interface UseDocumentResultsFilterStateParams {
  documentResults: DocumentTableRow[]
  facetColumnValues?: Partial<Record<FacetFilterId, string[]>>
  handleViewModeChange: (
    value: string | number,
    options?: { selectedDocumentIdWhenSwitchingToTable?: string | null }
  ) => void
  onSearchStateChange: (updates: SearchStateUpdate) => void
  isServerPaged?: boolean
  searchQuery: string
  searchStateFilters: SearchHistoryItem['searchState']['filters']
  selectedDocumentId: string | null
  viewMode: ViewMode
}

export interface UseDocumentResultsFilterStateResult {
  activeIndex: number
  cardFilters: CardFilterValues
  commitCardFiltersToUrl: () => void
  filteredDocuments: DocumentTableRow[]
  handleCardFiltersChange: (next: CardFilterValues) => void
  handleTableFiltersChange: (
    updater:
      | ColumnFiltersState
      | ((prev: ColumnFiltersState) => ColumnFiltersState)
  ) => void
  hasActiveFilters: boolean
  onViewModeChange: (value: string | number) => void
  setActiveIndex: React.Dispatch<React.SetStateAction<number>>
  tableColumns: ReturnType<typeof DocumentTableColumns>
  tableColumnUniqueValues: DocumentTableColumnUniqueValues
  tableFilters: ColumnFiltersState
  tableHasActiveFilters: boolean
}

function mergeFacetColumnValues(
  pageValues: DocumentTableColumnUniqueValues,
  facetColumnValues: Partial<Record<FacetFilterId, string[]>> | undefined
): DocumentTableColumnUniqueValues {
  return {
    vendor: facetColumnValues?.vendor ?? pageValues.vendor,
    docType: facetColumnValues?.docType ?? pageValues.docType,
  }
}

export function useDocumentResultsFilterState({
  documentResults,
  facetColumnValues,
  handleViewModeChange,
  onSearchStateChange,
  isServerPaged = false,
  searchQuery,
  searchStateFilters,
  selectedDocumentId,
  viewMode,
}: UseDocumentResultsFilterStateParams): UseDocumentResultsFilterStateResult {
  const { t } = useLingui()
  const {
    cardFilters,
    commitCardFiltersToUrl,
    handleCardFiltersEdit,
    handleTableFiltersChange,
    tableFilters,
  } = useDocumentSearchResultFilters({
    onSearchStateChange,
    isServerPaged,
    searchStateFilters,
  })

  const [activeIndex, setActiveIndex] = useState(0)
  const viewModeRef = useRef(viewMode)
  viewModeRef.current = viewMode

  useEffect(() => {
    setActiveIndex(0)
  }, [searchQuery])

  const filteredDocuments = useMemo(
    () =>
      isServerPaged || viewMode === 'table'
        ? documentResults
        : applyCardFilters(documentResults, cardFilters),
    [cardFilters, documentResults, isServerPaged, viewMode]
  )

  useEffect(() => {
    if (filteredDocuments.length === 0) {
      if (activeIndex !== 0) setActiveIndex(0)
      return
    }
    if (activeIndex < 0 || activeIndex >= filteredDocuments.length) {
      setActiveIndex(
        Math.min(Math.max(activeIndex, 0), filteredDocuments.length - 1)
      )
    }
  }, [activeIndex, filteredDocuments.length])

  const hasActiveFilters = useMemo(
    () =>
      tableFilters.some(({ value }) =>
        Array.isArray(value)
          ? value.length > 0
          : typeof value === 'string' && value.length > 0
      ),
    [tableFilters]
  )
  const tableHasActiveFilters = hasActiveFilters

  const handleCardFiltersChange = useCallback(
    (next: CardFilterValues) => {
      handleCardFiltersEdit(next)
      setActiveIndex(0)
    },
    [handleCardFiltersEdit]
  )

  const tableColumns = useMemo(() => DocumentTableColumns(t), [t])
  const tableColumnUniqueValues = useMemo(
    () =>
      mergeFacetColumnValues(
        getDocumentTableColumnUniqueValues(documentResults),
        facetColumnValues
      ),
    [documentResults, facetColumnValues]
  )

  const onViewModeChange = useCallback(
    (value: string | number) => {
      if (value === viewModeRef.current) return
      if (value === 'card') {
        const cardFilteredDocuments = isServerPaged
          ? documentResults
          : applyCardFilters(documentResults, cardFilters)
        if (selectedDocumentId) {
          const index = cardFilteredDocuments.findIndex(
            (result) => result.id === selectedDocumentId
          )
          setActiveIndex(index >= 0 ? index : 0)
        } else {
          setActiveIndex((prev) =>
            cardFilteredDocuments.length > 0
              ? Math.min(prev, cardFilteredDocuments.length - 1)
              : 0
          )
        }
      }
      handleViewModeChange(
        value,
        value === 'table'
          ? {
              selectedDocumentIdWhenSwitchingToTable:
                filteredDocuments[activeIndex]?.id ?? null,
            }
          : undefined
      )
    },
    [
      activeIndex,
      cardFilters,
      documentResults,
      filteredDocuments,
      handleViewModeChange,
      isServerPaged,
      selectedDocumentId,
    ]
  )

  return {
    activeIndex,
    cardFilters,
    commitCardFiltersToUrl,
    filteredDocuments,
    handleCardFiltersChange,
    handleTableFiltersChange,
    hasActiveFilters,
    onViewModeChange,
    setActiveIndex,
    tableColumns,
    tableColumnUniqueValues,
    tableFilters,
    tableHasActiveFilters,
  }
}
